/**
* @summary - File Upload Attachments
* @author Yuvasri S
* @since 2024-11-29
*/
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Box,
    Button,
    Stack,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import { DefaultCard } from '../../assets/styled-components/card';
import { getAuthToken } from "../../utility/tokenUtility";
import { MasterGraph } from '../../utility/apiURLS';
import CustomDialog from '../commoncomponents/CustomDialog';
import CustomAgGrid from '../commoncomponents/CustomAgGrid';
import { v4 as uuidv4 } from 'uuid';
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ContractManagementRepository from "../../api/repositories/contractManagementRepository";

const initialRow = {
    id: 1,
    file: null,
    documenttitlename: '',
    documenttitletype: '',
    filesize: '',
};


const ContractAttachment = forwardRef((props, ref) => {

    const location = useLocation();
    const contractMode = location?.state?.contractMode;
    const renewalMode = location?.state?.renewalmode;
    const viewMode = location?.state?.viewMode;
    const [rows, setRows] = useState((renewalMode || viewMode) ? [] : []);
    const [error, setError] = useState(false);
    const [attachmenterror, setAttachmentError] = useState("");
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const navigate = useNavigate();


    const token = getAuthToken("id_token");
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [gridApi, setGridApi] = useState(null);
    const [registeredFiles, setRegisteredFiles] = useState([]);
    const [storeFileName, setStoreFileName] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [deletedFilesArray, setDeletedFilesArray] = useState([]);
    const [deletedRowIds, setDeletedRowIds] = useState([])
    const [previouslyUploadedFiles, setPreviouslyUploadedFiles] = useState([]);



    const headerIdEdit = location?.state?.parentContractId || location?.state?.id;
    // const headerIdEdit = contractMode ? location?.state?.id : (location?.state?.femcontractregisterheaderId || location?.state?.bemcontractregisterheaderId)

    const fetchFiles = async (headerId) => {

        try {
            const fetchedFiles = await ContractManagementRepository.fetchAttachments(location?.state?.serviceDesc || props.service, headerId)
            // Assuming the response is an array of files
            // setRows(viewMode ? fetchedFiles : (fetchedFiles.length > 0 ? fetchedFiles : [initialRow]));
            if (fetchedFiles?.length > 0) {
                setRows(fetchedFiles);
            } else if (viewMode == true) {
                setRows(fetchedFiles);
            }
            else {
                // const newRow = {
                //     id: 1,
                //     category: '',
                //     fileType: '',
                //     designation: '',
                //     size: '',
                // };
                setRows([]);
            }

        } catch (error) {
            console.error('Error fetching files:', error);
        }
    };

    useEffect(() => {
        if (headerIdEdit) {
            fetchFiles(headerIdEdit);
        }
    }, [headerIdEdit]);

    const handleDeleteClick = (id) => () => {
        // Find the row to delete by its ID
        const rowToDelete = rows.find(row => row.id === id);

        // Check if either file is null or category is an empty string
        if (rowToDelete && (rowToDelete.file === null || rowToDelete.category === "")) {
            // If either condition is met, delete immediately without confirmation
            const updatedRows = rows.filter((row) => row.id !== id);
            setRows(updatedRows);

            // Move the deleted file to `deletedFilesArray` if applicable
            const fileToDelete = selectedFiles.find((file) => file.id === id);
            if (fileToDelete) {
                setDeletedFilesArray((prevDeletedFiles) => [...prevDeletedFiles, fileToDelete]);  // Add to deleted files array
            }

            // Remove the deleted file from `selectedFiles`
            const updatedSelectedFiles = selectedFiles.filter((file) => file.id !== id);
            setSelectedFiles(updatedSelectedFiles);  // Update selectedFiles array

            // Remove the file from `previouslyUploadedFiles` if it was already uploaded
            const updatedPreviouslyUploadedFiles = previouslyUploadedFiles.filter(file => file.id !== id);
            setPreviouslyUploadedFiles(updatedPreviouslyUploadedFiles);

            // If no rows are left, add a default empty row
            if (updatedRows.length === 0) {
                setRows([initialRow]);  // Add default empty row if no rows are left
            }
        } else {
            // If neither condition is met, open the delete confirmation dialog
            setSelectedId(id);
            setDeleteDialogOpen(true);
        }
    };
    const handleDeleteConfirm = async () => {
        const selectedRow = rows.find(row => row.id === selectedId);
        if (selectedRow) {
            try {
                if (typeof selectedId === 'string') {
                    const response = await ContractManagementRepository.deleteAttachments(location?.state != null ? location?.state?.serviceDesc : props.service, selectedId);

                    if (response.status >= 200 && response.status <= 204) {
                        const updatedRows = rows.filter((row) => row.id !== selectedId);
                        setRows(updatedRows);
                        const updatedSelectedFiles = selectedFiles.filter(file => file.id !== selectedId);
                        setSelectedFiles(updatedSelectedFiles);
                        // fetchFiles();
                    } else {
                        throw new Error('Failed to delete record');
                    };
                } else if (typeof selectedId === 'number') {
                    const updatedRows = rows.filter((row) => row.id !== selectedId);

                    if (updatedRows.length === 0) {
                        // If no rows remain, set the initial row
                        setRows([initialRow]);
                    } else {
                        // Set the updated rows
                        setRows(updatedRows);
                    }

                    // Update the selected files array
                    const updatedSelectedFiles = selectedFiles.filter(file => file.id !== selectedId);
                    setSelectedFiles(updatedSelectedFiles);

                    // Optional: Fetch files if rows are cleared
                    if (updatedRows.length === 0) {
                        fetchFiles();
                    }
                }
            } catch (error) {
                setError(true);
                setErrorMessage('Error deleting attachment. Please try again.');
            }
        }
        setDeleteDialogOpen(false);
    };



    const handleDeleteDialogClose = () => {
        setDeleteDialogOpen(false);
    };

    const handleSuccessDialogClose = () => {
        setSuccessDialogOpen(false);
    };

    const handleAddRow = () => {
        const id =
            rows.length > 0
                ? Math.max(...rows.map((row) => (isNaN(row.id) ? 0 : Number(row.id)))) + 1
                : 1;
        const newRow = {
            id: id,
            file: null,
            documenttitlename: '',
            documenttitletype: '',
            filesize: '',
        };
        setRows([...rows, newRow]);
    };

    const handleFileChange = (event, id) => {
        const file = event.target.files[0];
        if (!file) return;
        const fileName = file.name;
        const fileExtension = fileName.split('.').pop().toLowerCase();
        if (file.size > 20 * 1024 * 1024) {
            setError(true);
            setErrorMessage('Error: File size exceeds 20 MB. Please select smaller files.');
            return;
        }

        const allowedFileExtensions = ['doc', 'jpeg', 'jpg', 'png', 'xlsx', 'csv', 'pdf', 'docx'];
        if (!allowedFileExtensions.includes(fileExtension)) {
            setError(true);
            setErrorMessage('Error: Only DOC, JPEG, JPG, PNG, XLSX, CSV, and PDF files are allowed.');
            return;
        }

        const fileNameWithoutExtension = fileName.replace(new RegExp(`.${fileExtension}$`), '');

        let updatedSelectedFiles = [...selectedFiles];
        const fileIndex = updatedSelectedFiles.findIndex((file) => file.id === id);
        if (fileIndex > -1) {
            updatedSelectedFiles[fileIndex] = { id, file };
        } else {
            updatedSelectedFiles.push({ id, file });
        }
        setSelectedFiles(updatedSelectedFiles);

        const updatedRows = rows.map((row) => {
            if (row.id === id) {
                let fileSize;
                if (file.size >= 1024 * 1024) {
                    fileSize = (file.size / (1024 * 1024)).toFixed(2) + ' MB';
                } else {
                    fileSize = (file.size / 1024).toFixed(2) + ' KB';
                }

                return {
                    ...row,
                    file,
                    documenttitlename: fileNameWithoutExtension,
                    documenttitletype: fileExtension,
                    filesize: fileSize,
                };
            }
            return row;
        });

        setRows(updatedRows);
        setHasUnsavedChanges(true);

        // Debug: log the updated rows state
    };



    const handleDownload = async (file) => {
        try {
            // Perform the GET request to download the file
            const response = await axios.get(`${MasterGraph}/api/FileUpload/DownloadFile/${file.filename}`, {
                ...config,
                responseType: 'blob'
            });

            if (response) {
                const url = URL.createObjectURL(response.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = file.documenttitlename || file.filename; // Use documenttitle as the filename
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }
            else {
                if (file && file instanceof Blob) {
                    const url = URL.createObjectURL(file);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = file.documenttitlename || file.filename; // Use documenttitle as the filename
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                } else {
                    console.error('Invalid file object:', file);
                }
            }
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };



    const handleDownloadClick = (event, file) => {
        event.preventDefault();
        handleDownload(file);
    };
    function uuidv4(extension) {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        }) + '.' + extension;
    }

    const uploadFiles = async (formData) => {
        try {
            let storeFileNameArray = [];
            let storeregsitredFilesResponse = [];
            let storefileUploadResponse = [];
            for (let i = 0; i < selectedFiles?.length; i++) {
                const formData = new FormData();
                const extension = selectedFiles[i]?.file?.name.split('.').pop();
                const fileName = uuidv4(extension); // generate a unique id
                formData.append('fileObj', selectedFiles[i]?.file, fileName); // append file with unique id as filename
                storeFileNameArray.push({ id: fileName, filename: `${selectedFiles[i]?.file?.name}`, selectedFileId: selectedFiles[i]?.id });

                try {
                    const fileUploadResponse = await axios.post(`${MasterGraph}/api/fileupload`, formData, config);
                    storeregsitredFilesResponse.push(fileUploadResponse.data);
                    storefileUploadResponse.push(fileUploadResponse.data);
                } catch (error) {
                    console.log('Error uploading file:', error.response ? error.response.data : error.message);
                }
            }
            setStoreFileName(storeFileNameArray);
            setRegisteredFiles(storeregsitredFilesResponse);
            return { storefileUploadResponse, storeFileNameArray };
        } catch (error) {
            console.error('Save error:', error);
        }
    };

    const saveFilesToAttachment = async (getService, payload) => {
        try {
            const response = ContractManagementRepository.createAttachments(getService, payload)
            if (response.status >= 200 && response.status <= 204) {
                setOpenDialog(true);
            }
            return response;
        } catch (error) {
            console.error('Save error:', error);
        }
    };


    const handleSaveClick = async (headerId) => {

        const isValid = handleValidation();

        const getService = location?.state?.createMode ? props.service : location?.state?.serviceDesc;
        const serviceType = getService === 'FEMS' ? 'fem' : 'bem';


        if (isValid) {
            try {
                const filteredSelectedFiles = selectedFiles
                    .filter(file => rows.some(row => row.id === file.id))
                    .filter(file => !previouslyUploadedFiles.some(uploaded => uploaded.id === file.id));

                if (filteredSelectedFiles.length > 0) {
                    const { storefileUploadResponse, storeFileNameArray } = await uploadFiles(filteredSelectedFiles);

                    if (storefileUploadResponse?.length > 0) {
                        let storefileUploadResponseFinal = [];

                        for (let file of storefileUploadResponse) {
                            const documentTitle = storeFileNameArray.find(item => item.id === file.fileName)?.filename;
                            const filedesc = documentTitle.split('.').pop().toLowerCase();

                            if (documentTitle) {
                                const payload = {
                                    filename: file.fileName,
                                    filesize: file.fileSize,
                                    filepath: "D:/IFAC-X/UploadFolder",
                                    documenttitle: documentTitle,
                                    filedesc: filedesc,
                                    documentType: documentTitle.split(".")[1].toUpperCase(),
                                    [`${serviceType}contractregisterheaderId`]: headerId,

                                };

                                try {
                                    const response = await saveFilesToAttachment(location?.state?.serviceDesc || props.service, payload);
                                    setHasUnsavedChanges(false);
                                    if (response) {
                                        storefileUploadResponseFinal.push(response);
                                        await fetchFiles(response?.bemcontractregisterheaderId || response?.femcontractregisterheaderId);
                                    } else {
                                        console.log("Error saving file:", file.fileName);
                                    }
                                } catch (error) {
                                    console.error("Error saving file:", error);
                                }
                            } else {
                                console.log("Document Title not found for file:", file.fileName);
                            }
                        }

                        if (storefileUploadResponseFinal.length === storefileUploadResponse.length) {
                            setOpenDialog(true);
                            setPreviouslyUploadedFiles(prev => [...prev, ...filteredSelectedFiles]);
                        } else {
                            console.log("Error in uploading some files");
                        }
                    } else {
                        console.log("No files registered");
                    }
                } else {
                    console.log("No files selected for upload");
                }

                // Handle deletions
                if (deletedRowIds.length > 0) {
                    for (let id of deletedRowIds) {
                        try {
                            await ContractManagementRepository.deleteAttachments(location?.state?.serviceDesc || props.service, id);
                        } catch (error) {
                            console.error(`Error deleting row with ID ${id}:`, error);
                        }
                    }

                    setDeletedRowIds([]);
                    setSuccessMessage("Data saved successfully.");
                    setSuccessDialogOpen(true);
                }

                if (deletedFilesArray.length > 0) {
                    setDeletedFilesArray([]);
                }
            } catch (error) {
                console.error("Error registering files:", error);
            }
        }

    };


    const [Attachmentvalidation, setAttachmentvalidation] = useState(false);
    const handleValidation = () => {
        // Check the first row (rows[0]) for missing file or invalid values
       

        if ((rows?.length < 1 || rows[0]?.filesize === "" )) {
            setAttachmentError("Please add at least one Attachment.");
            setAttachmentvalidation(true);
            return false;
        }

        // If the first row is valid, clear any previous errors
        setAttachmentvalidation(false);
        setAttachmentError(""); // Clear error if the first row is valid
        return true;
    };


    // Expose handleSubmit to the parent via ref
    useImperativeHandle(ref, () => ({
        handleSaveClick,
        fetchFiles,
        Attachmentvalidation,
    }));

    useEffect(() => {
        handleValidation();
    }, [rows])


    const handleCancel = () => {
        if (hasUnsavedChanges && !viewMode) {
            setCancelDialogOpen(true);
        } else {
            navigate(-1, { replace: true });
        }
    };

    const handleCancelDialogClose = () => {
        setCancelDialogOpen(false);
    };

    const handleCancelDialogConfirm = () => {
        setCancelDialogOpen(false);
        navigate(-1, { replace: true });
    };


    const handleSnackbarClose = () => {
        setError(false);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };
    return (
        <>

            <DefaultCard sx={{ height: '100%' }}>
                <Typography variant='title2'>Attachments</Typography>
                <Box mb={2} />
                <form style={{ position: 'relative' }}>
                    <Stack>
                        <Typography variant="labelSm" mt={0}>
                            Please add files in format of doc, jpg, png, pdf, xlsx, csv and
                            file size should be less than 20 MB.
                        </Typography>
                    </Stack>
                    {attachmenterror && (
                        <Typography component={"span"} color={"error"}>
                            {attachmenterror}*
                        </Typography>
                    )}
                    {!viewMode && (
                        <Stack direction={"row"} justifyContent={"end"}>
                            <Stack mb={1}>
                                <Button variant="contained" onClick={handleAddRow}>
                                    <AddRoundedIcon />
                                    Add File
                                </Button>
                            </Stack>
                        </Stack>
                    )}

                    <CustomAgGrid

                        rows={rows}
                        columns={[
                            {
                                field: 'file',
                                headerName: 'File',
                                flex: 1,
                                cellRenderer: (params) => (
                                    <Button variant="outlined"
                                        component="label"
                                        size="small"
                                        sx={{
                                            width: '100px',
                                            height: '38px',
                                            padding: '4px 8px',
                                            fontSize: '0.75rem',
                                            display: 'flex',
                                            justifyContent: 'center',
                                        }}
                                        disabled={params.data?.documenttitlename || viewMode}>
                                        {params.data.documenttitlename ? 'FILE UPLOADED' : 'BROWSE FILE'}
                                        <input
                                            type="file"
                                            hidden
                                            onChange={(event) => handleFileChange(event, params?.data?.id)}
                                        />
                                    </Button>
                                ),
                            },
                            {
                                field: 'documenttitlename',
                                headerName: 'File Name',
                                flex: 1,
                            },
                            {
                                field: 'documenttitletype',
                                headerName: 'File Type',
                                flex: 1,
                            },
                            {
                                field: 'filesize',
                                headerName: 'Size',
                                flex: 1,
                            },
                            {
                                field: 'actions',
                                type: 'actions',
                                headerName: 'Actions',
                                flex: 1,
                                cellClassName: 'actions',
                                cellRenderer: (params) => (
                                    <>
                                        <Button onClick={handleDeleteClick(params?.data?.id)} disabled={renewalMode || viewMode}>
                                            <DeleteIcon />
                                        </Button>
                                        <Button onClick={(event) => handleDownloadClick(event, params.data)} disabled={renewalMode}>
                                            {typeof (params?.data?.id) === 'string' ? <DownloadIcon /> : <></>}
                                        </Button>
                                    </>
                                ),
                            },
                        ]}
                        setGridApi={setGridApi}
                    />
                </form>
            </DefaultCard>

            <CustomDialog
                open={error}
                onClose={handleSnackbarClose}
                content={errorMessage}
                confirmBtn="OK"
                onConfirm={() => setError(false)}
            />
            <CustomDialog
                open={cancelDialogOpen}
                onClose={handleCancelDialogClose}
                title="Unsaved Changes"
                content="Record(s) not Saved/Updated. Do you want to continue?"
                confirmBtn="Yes"
                cancelBtn="No"
                onConfirm={handleCancelDialogConfirm}
                onCancel={handleCancelDialogClose}
            />
            <CustomDialog
                open={deleteDialogOpen}
                onClose={handleDeleteDialogClose}
                title="Confirm Delete"
                content="Are you sure you want to delete?"
                confirmBtn="Yes"
                cancelBtn="No"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteDialogClose}
            />

            <CustomDialog
                open={successDialogOpen}
                onClose={handleSuccessDialogClose}
                content={successMessage}
                confirmBtn="OK"
                onConfirm={handleSuccessDialogClose}
            />
        </>
    );
})

export default ContractAttachment;
